import { useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { GroupedVirtuoso } from 'react-virtuoso';
import { groupBy } from 'lodash';

import { Text, Search } from '@bizzabo/design-system';
import { User } from '@bizzabo/entities';

import ContactListItem from '../ContactListItem';

interface ContactListProps {
    eventId: number;
    users: User[];
    searchTerm: string;
    loadMore: () => void;
    onContactItemClick: (attendeeId: number) => void;
}

const OVERSCAN = 148; //increases the visual window by pixels. item height is 74

const ContactList: React.FC<ContactListProps> = ({
    eventId,
    users,
    searchTerm,
    loadMore,
    onContactItemClick,
}) => {
    const classes = useStyles();
    const [isScrolling, setIsScrolling] = useState<boolean>(false);

    const groupedUsers = groupBy(users, (user: User) => user.firstName?.[0].toUpperCase());
    const groupCounts = Object.values(groupedUsers).map((users: User[]) => users.length);
    const groups = Object.keys(groupedUsers);

    const loadMoreHandle = () => {
        if (!isScrolling) {
            return;
        }
        loadMore();
    };

    const emptyPlaceholder = () => {
        if (!searchTerm) {
            return null;
        }
        return (
            <div className={classes.emptyPlaceholder}>
                <div className={classes.searchIcon}>
                    <Search />
                </div>
                <Text variant="t81">No matching people</Text>
            </div>
        );
    };

    const groupContent = (index: number) => {
        return (
            <Text variant="t80" className={classes.groupContent}>
                {groups[index]}
            </Text>
        );
    };

    return (
        <div className={classes.virtuosoContainer}>
            <div className={classes.aToZList}>
                <Text variant="t81" className={classes.aToZListText}>
                    Attendees A-Z:
                </Text>
            </div>
            <GroupedVirtuoso
                isScrolling={setIsScrolling}
                groupCounts={groupCounts}
                endReached={loadMoreHandle}
                groupContent={groupContent}
                itemContent={index => (
                    <ContactListItem
                        eventId={eventId}
                        user={users[index]}
                        onClick={onContactItemClick}
                    />
                )}
                components={{ EmptyPlaceholder: emptyPlaceholder }}
                overscan={OVERSCAN}
                className={classes.virtuosoComponent}
            />
        </div>
    );
};

const useStyles = makeStyles(
    theme => ({
        virtuosoContainer: {
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            backgroundColor: theme.palette.greys[100],
            borderRadius: '0 0 33px 33px',
            overflowX: 'hidden',
        },
        virtuosoComponent: {
            flexGrow: 1,
        },
        header: {
            padding: '10px 24px',
            color: theme.palette.greys[700],
        },
        groupContent: {
            height: 18,
            display: 'flex',
            alignItems: 'center',
            paddingLeft: 24,
            color: theme.palette.greys[700],
            backgroundColor: theme.palette.greys[200],
        },
        emptyPlaceholder: {
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
        },
        searchIcon: {
            width: 40,
            height: 40,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 16,
            borderRadius: '50%',
            backgroundColor: theme.palette.greys[400],
            color: 'white',
        },
        aToZList: {
            marginBottom: 16,
            marginLeft: 24,
        },
        aToZListText: {
            color: '#767676',
        },
    }),
    { classNamePrefix: ContactList.name }
);

export default ContactList;
